import { useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import ENV from '~/config/env';
import { setRouteChangeConfigForRepeatUsers } from '../helpers';
import { addTimingHeadersForFetch } from './addTimingHeadersForFetch';
import { ANALYTICS_PAGE_LOAD_IGNORE_PATHS } from '../config/constants';

const thirdPartyUrlSegments = [
  'mt-fe-dc.mindtickle.com',
  'snowplowanalytics.snowplow',
  'wchat.freshchat.com',
  'webpush.freshchat.com',
  'fast.appcues.com',
  'api.appcues.net',
  'zipy.ai',
  'whatfix.com',
];

const tracingNotAllowedUrls = [
  'mt-fe-dc',
  'sp-coll',
  'assets', // exclude cdn for mt-translations calls to avoid pre-flights,
  'mindtickle-data-platform-sisense', // to exclude DD for sisense calls
];

type TInitializeDatadogProps = {
  disableDatadog: boolean | undefined;
};

// eslint-disable-next-line complexity
const InitializeDatadog = ({ disableDatadog = false }: TInitializeDatadogProps) => {
  const datadogAlreadyInitialized = useRef(false);
  //eslint-disable-next-line
  if (!datadogAlreadyInitialized.current && !disableDatadog) {
    if (!(ENV.DATADOG_APPLICATION_ID && ENV.DATADOG_CLIENT_TOKEN)) {
      return null;
    }
    datadogRum.init({
      applicationId: ENV.DATADOG_APPLICATION_ID,
      clientToken: ENV.DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: ENV.APPLICATION,
      env: ENV.TRACK,
      version: '1.0.0',
      sampleRate: disableDatadog ? 0 : parseInt(ENV.DATADOG_SAMPLE_RATE),
      sessionReplaySampleRate: parseInt(ENV.DATADOG_SESSION_REPLAY_SAMPLE_RATE),
      trackInteractions: true,
      trackFrustrations: true,
      trackResources: true,
      trackLongTasks: true,
      useSecureSessionCookie: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingOrigins: [
        origin => {
          let allowed = origin.includes('mindtickle.com');
          if (allowed) {
            allowed = !tracingNotAllowedUrls.some(url => origin.includes(url));
          }
          return allowed;
        },
      ],
      excludedActivityUrls: [
        ...ANALYTICS_PAGE_LOAD_IGNORE_PATHS,
        // @ts-ignore
        url => {
          const exclude = thirdPartyUrlSegments.some(urlSegment => url.includes(urlSegment));
          return exclude;
        },
      ],
      //eslint-disable-next-line
      beforeSend(event, context) {
        setRouteChangeConfigForRepeatUsers(event, context);
        addTimingHeadersForFetch(event, context);
      },
    });

    // PII compliance
    // todo - remove sessionReplaySampleRate and env. vars
    // datadogRum.startSessionReplayRecording();

    datadogAlreadyInitialized.current = true;
  }
  return null;
};

export default InitializeDatadog;
